import { useEffect, useState, type ReactNode } from "react";
import { COLORS } from "../lib/brand";
import { Platform, StyleSheet, View } from "react-native";
import Animated, { Easing, useAnimatedStyle, useSharedValue, withTiming } from "react-native-reanimated";
import Svg, { Circle, Line } from "react-native-svg";

// `react-dom` is web-only. Import it behind a Platform gate so the native
// bundles (iOS / Android) don't try to resolve it.
let createPortal: ((node: ReactNode, container: Element) => ReactNode) | null = null;
if (Platform.OS === "web") {
  // eslint-disable-next-line @typescript-eslint/no-require-imports
  createPortal = require("react-dom").createPortal;
}

const AMBER = COLORS.AMBER.hex;
const SAGE = COLORS.SAGE.hex;

const BASE_SIZE = 28;
const HOVER_SIZE = 36;

const STYLE_ID = "coravu-cursor-style";
const BODY_CLASS = "coravu-cursor-on";
/** Body-class-scoped global rule. Keeping the class explicit makes the
 *  behavior debuggable in DevTools (toggle the class off and the native
 *  cursor returns) and easy to disable per-surface in the future without
 *  fighting `* { ... !important }` everywhere. The selector still wins
 *  over per-element inline `cursor: pointer` because of !important. */
const GLOBAL_CSS = `
body.${BODY_CLASS}, body.${BODY_CLASS} * { cursor: none !important; }
body.${BODY_CLASS} input,
body.${BODY_CLASS} textarea,
body.${BODY_CLASS} select,
body.${BODY_CLASS} [contenteditable],
body.${BODY_CLASS} [contenteditable="true"] {
  cursor: none !important;
}
`;

// INT32_MAX — sits above any plausible z-index battle with modal stacks
// (RN web's Modal portal, react-native-portalize, third-party tooltips).
const TOP_Z = 2147483647;

function isInteractiveTarget(target: EventTarget | null): boolean {
  if (typeof document === "undefined" || !(target instanceof Element)) return false;
  return !!target.closest?.('a, button, [role="button"], input, textarea, select');
}

function detectTouch(): boolean {
  if (typeof window === "undefined") return false;
  return "ontouchstart" in window || (navigator?.maxTouchPoints ?? 0) > 0;
}

/**
 * Web-only custom cursor — the Coravu wheel mark, eased smooth-follow.
 * Mounted in the root layout so it appears on every web page (auth,
 * marketing, all portals). Injects a body-class-scoped stylesheet that
 * hides the native cursor on every element including text inputs — the
 * blinking text caret inside inputs is a separate concern (`caret-color`)
 * and still works, so users can see where they're typing.
 *
 * Renders via React DOM portal into document.body so the wheel sits as a
 * top-level DOM sibling of any RN <Modal> (which itself portals to body
 * on web). DOM order alone keeps the cursor above modal content even
 * when both stack at the same z-index.
 *
 * On touch devices the component returns null and skips both the
 * stylesheet injection and the body class.
 */
export function CursorFollower() {
  const x = useSharedValue(-100);
  const y = useSharedValue(-100);
  const hover = useSharedValue(0);

  // Touch detection has to read window/navigator, so it runs in an effect.
  // Render once with isTouch=false then bail in the effect if we are on a
  // touch device — keeps SSR-safe and avoids hydration mismatch.
  const [isTouch, setIsTouch] = useState(false);

  useEffect(() => {
    if (Platform.OS !== "web" || typeof document === "undefined") return;
    if (detectTouch()) {
      setIsTouch(true);
      return;
    }

    // Inject the global cursor stylesheet once. ID guard means re-mounts
    // (dev fast-refresh, layout shuffles) don't duplicate the rule.
    let injected: HTMLStyleElement | null = null;
    if (!document.getElementById(STYLE_ID)) {
      injected = document.createElement("style");
      injected.id = STYLE_ID;
      injected.textContent = GLOBAL_CSS;
      document.head.appendChild(injected);
    }
    document.body.classList.add(BODY_CLASS);

    const onMove = (e: MouseEvent) => {
      const tx = e.clientX;
      const ty = e.clientY;
      // Mousemove fires ~60Hz; wrapping each event in withTiming caused
      // tween-restart storms and the cursor never caught up. Snap directly.
      x.value = tx;
      y.value = ty;
      const el = document.elementFromPoint(tx, ty);
      const over = isInteractiveTarget(el);
      hover.value = withTiming(over ? 1 : 0, { duration: 150, easing: Easing.out(Easing.quad) });
    };

    document.addEventListener("mousemove", onMove, { passive: true });
    return () => {
      document.removeEventListener("mousemove", onMove);
      document.body.classList.remove(BODY_CLASS);
      if (injected && injected.parentNode) injected.parentNode.removeChild(injected);
    };
  }, [x, y, hover]);

  const wheelStyle = useAnimatedStyle(() => {
    // Animate hover via transform scale, not width/height — mutating layout
    // every frame triggers RN-Web reflow which is what was producing the
    // Y-axis jitter. Translate first, then scale, so the wheel grows around
    // its center (the BASE_SIZE/2 offset locks the centering point).
    const scale = 1 + hover.value * (HOVER_SIZE / BASE_SIZE - 1);
    return {
      position: "absolute" as const,
      left: 0,
      top: 0,
      width: BASE_SIZE,
      height: BASE_SIZE,
      transform: [
        { translateX: x.value - BASE_SIZE / 2 },
        { translateY: y.value - BASE_SIZE / 2 },
        { scale },
      ],
    };
  });

  if (Platform.OS !== "web" || isTouch) return null;
  if (!createPortal || typeof document === "undefined") return null;

  return createPortal(
    <View pointerEvents="none" style={[StyleSheet.absoluteFillObject, { zIndex: TOP_Z }]}>
      <Animated.View pointerEvents="none" style={wheelStyle}>
        <Svg width="100%" height="100%" viewBox="0 0 34 34" fill="none">
          {/* Concentric compass — same glyph as the portal sidebar logo. No
              FOREST_MID background <Rect> so the wheel floats over the page. */}
          <Circle cx={17} cy={17} r={9.5} stroke={SAGE} strokeWidth={0.7} fill="none" />
          <Circle cx={17} cy={17} r={5.5} stroke={AMBER} strokeWidth={1.2} fill="none" />
          <Circle cx={17} cy={17} r={1.8} fill={AMBER} />
          <Line x1={17} y1={5} x2={17} y2={8.5} stroke={SAGE} strokeWidth={0.7} />
          <Line x1={17} y1={25.5} x2={17} y2={29} stroke={SAGE} strokeWidth={0.7} />
          <Line x1={5} y1={17} x2={8.5} y2={17} stroke={SAGE} strokeWidth={0.7} />
          <Line x1={25.5} y1={17} x2={29} y2={17} stroke={SAGE} strokeWidth={0.7} />
        </Svg>
      </Animated.View>
    </View>,
    document.body
  );
}
